import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import TrackerShoppingListQuery from './TrackerShoppingListQuery';
import type { ShoppingListRange, TrackerShoppingList } from './types';

type QueryData = { dailyTracker: { shoppingList: TrackerShoppingList | null } };

export type TrackerShoppingListConnectorResult = {
  loading: boolean;
  shoppingList: TrackerShoppingList | null;
  /** Variables of the query currently on screen — `{}` until the user picks a range. */
  variables: Partial<ShoppingListRange>;
  showRange: (range: ShoppingListRange) => void;
};

interface ITrackerShoppingListConnectorApolloProps {
  children: (result: TrackerShoppingListConnectorResult) => React.ReactElement;
}

/**
 * Reads the tracker shopping list. Starts without dates so the server resumes the user's
 * last range (or today..+6); `showRange` switches to an explicit range.
 */
const TrackerShoppingListConnectorApollo: React.FC<ITrackerShoppingListConnectorApolloProps> = ({
  children,
}) => {
  const [range, setRange] = useState<ShoppingListRange | null>(null);
  const variables: Partial<ShoppingListRange> = range ?? {};

  const { loading, data, error } = useQuery<QueryData>(TrackerShoppingListQuery, {
    variables,
    fetchPolicy: 'cache-and-network',
  });

  // No fallback to the previous range's data: its rows would stay tappable while
  // the screen already belongs to the new range.
  return children({
    loading: loading && !data,
    shoppingList: error ? null : data?.dailyTracker?.shoppingList ?? null,
    variables,
    showRange: setRange,
  });
};

export default TrackerShoppingListConnectorApollo;
